import React from 'react';
import { Link, Text } from '@Sage/hoi-poi-ui';

import { getDateFromBackendISO } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

function formatValue(value) {
    return value != null && String(value).trim() ? String(value) : '—';
}

function formatDate(timestamp) {
    if (!timestamp) return '—';
    const date = getDateFromBackendISO(timestamp, 'LL');
    const time = getDateFromBackendISO(timestamp, 'LT');
    return getLiteralWithParameters('label_date_with_time', [date, time]);
}

// Cell Renderers - with vertical centering
const cellWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
};

const RecipientNameCell = ({ value }) => (
    <div style={cellWrapperStyle}>
        <Text bold isTruncated>
            {formatValue(value)}
        </Text>
    </div>
);

const RecipientEmailCell = ({ value }) => (
    <div style={cellWrapperStyle}>
        <Text isTruncated>{formatValue(value)}</Text>
    </div>
);

const RecipientDateCell = ({ value }) => (
    <div style={cellWrapperStyle}>
        <Text>{formatDate(value)}</Text>
    </div>
);

const RecipientDescriptionCell = ({ value }) => (
    <div style={cellWrapperStyle}>
        <Text isTruncated>{formatValue(value)}</Text>
    </div>
);

const RecipientLinkCell = ({ data }) => {
    const url = data?.url || data?.link || data?.href || '';
    return (
        <div style={cellWrapperStyle}>
            {url ? (
                <Link
                    className="fm-campaign-details__event-link"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    isTruncated
                >
                    {url}
                </Link>
            ) : (
                <Text>—</Text>
            )}
        </div>
    );
};

/**
 * Get column definitions for SectionList based on event type.
 * @param {string} eventType
 * @returns {Array<Object>} AG Grid column definitions
 */
export function getColumns(eventType) {
    const COLUMN_DEFINITIONS = {
        default: [
            {
                colId: 'name',
                headerName: getLiteral('label_name'),
                field: 'name',
                cellRenderer: 'RecipientNameCell',
                flex: 1,
                minWidth: 180,
            },
            {
                colId: 'email',
                headerName: getLiteral('label_email_address'),
                field: 'email',
                cellRenderer: 'RecipientEmailCell',
                flex: 1,
                minWidth: 220,
            },
            {
                colId: 'timestamp',
                headerName: getLiteral('label_sent_date'),
                field: 'timestamp',
                cellRenderer: 'RecipientDateCell',
                width: 180,
                minWidth: 160,
            },
        ],
        bounce: [
            {
                colId: 'name',
                headerName: getLiteral('label_name'),
                field: 'name',
                cellRenderer: 'RecipientNameCell',
                flex: 1,
                minWidth: 180,
            },
            {
                colId: 'email',
                headerName: getLiteral('label_email_address'),
                field: 'email',
                cellRenderer: 'RecipientEmailCell',
                flex: 1,
                minWidth: 220,
            },
            {
                colId: 'description',
                headerName: getLiteral('label_description'),
                field: 'bounceReason',
                cellRenderer: 'RecipientDescriptionCell',
                flex: 1,
                minWidth: 200,
            },
            {
                colId: 'timestamp',
                headerName: getLiteral('label_sent_date'),
                field: 'timestamp',
                cellRenderer: 'RecipientDateCell',
                width: 180,
                minWidth: 160,
            },
        ],
    };
    return COLUMN_DEFINITIONS[eventType] ?? COLUMN_DEFINITIONS.default;
}

/**
 * Get cell renderer components for SectionList.
 * @returns {Object} Map of cell renderer names to components
 */
export function getComponents() {
    return {
        RecipientNameCell,
        RecipientEmailCell,
        RecipientDateCell,
        RecipientDescriptionCell,
        RecipientLinkCell,
    };
}
